import React, { useEffect, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import Swal from 'sweetalert2';
import useLoader from 'hooks/useLoader';
import { copyRun, getRuns } from 'data/repositories/MigrationRunbook';
import { MigrationRun } from 'data/schemas/MigrationRunbook';
import useRunbookIdentity, { actor } from './useRunbookIdentity';

const statuses = [
    ['', 'All'],
    ['draft', 'Draft'],
    ['scheduled', 'Scheduled'],
    ['in_progress', 'In progress'],
    ['completed', 'Completed'],
    ['rollback_in_progress', 'Rollback'],
    ['rolled_back', 'Rolled back'],
    ['cancelled', 'Cancelled']
];

const badge = (status: string) =>
    status === 'completed'
        ? 'bg-emerald-100 text-emerald-800'
        : status.includes('rollback')
          ? 'bg-red-100 text-red-800'
          : status === 'in_progress'
            ? 'bg-blue-100 text-blue-800'
            : status === 'cancelled'
              ? 'bg-gray-200 text-gray-700'
              : 'bg-amber-100 text-amber-800';

const formatWindow = (value?: string) =>
    value
        ? new Date(value).toLocaleString('en-GB', {
              dateStyle: 'medium',
              timeStyle: 'short',
              timeZone: 'Asia/Bangkok'
          })
        : 'Not scheduled';

export default function MigrationRunbookList() {
    const identity = useRunbookIdentity();
    const loader = useLoader();
    const history = useHistory();
    const [runs, setRuns] = useState<MigrationRun[]>([]);
    const [status, setStatus] = useState('');
    const [search, setSearch] = useState('');

    const load = async () => {
        if (!identity.citizenId) return;
        try {
            loader.show();
            setRuns(
                await getRuns({
                    status,
                    search,
                    viewerCitizenId: identity.citizenId,
                    viewerTeams: identity.teams.join(',')
                })
            );
        } catch (error) {
            Swal.fire('Runbook error', (error as Error).message, 'error');
        } finally {
            loader.hide();
        }
    };

    useEffect(() => {
        load();
    }, [identity.citizenId, identity.teams.join(','), status]);

    const openRun = (run: MigrationRun) => {
        const canPlan = run.role === 'run_owner' || run.role === 'editor';
        history.push(
            run.status === 'draft' && canPlan
                ? `/migration-runbook/runs/${run.id}/manage`
                : `/migration-runbook/runs/${run.id}`
        );
    };

    return (
        <div className="min-h-screen bg-slate-100 px-4 py-6 md:px-6">
            <div className="mx-auto max-w-7xl">
                <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
                    <div>
                        <h1 className="text-2xl font-bold text-slate-950">Migration Runbook</h1>
                        <p className="mt-1 text-sm text-gray-500">
                            Plan, execute, and track every migration step with your team.
                        </p>
                    </div>
                    <Link
                        className="rounded-md bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-blue-700"
                        to="/migration-runbook/new"
                    >
                        + Create migration
                    </Link>
                </div>

                <section className="mb-4 rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                    <div className="flex flex-wrap gap-2">
                        {statuses.map(([value, label]) => (
                            <button
                                key={value}
                                type="button"
                                onClick={() => setStatus(value)}
                                className={`rounded-full px-3 py-1.5 text-sm font-medium ${
                                    status === value
                                        ? 'bg-slate-900 text-white'
                                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                                }`}
                            >
                                {label}
                            </button>
                        ))}
                    </div>
                    <div className="mt-4 flex gap-2">
                        <input
                            value={search}
                            onChange={event => setSearch(event.target.value)}
                            onKeyDown={event => event.key === 'Enter' && load()}
                            placeholder="Search migration or owner"
                            className="min-w-0 flex-1 rounded-md border border-gray-300 px-3 py-2.5 text-sm outline-none focus:border-blue-500"
                        />
                        <button
                            type="button"
                            onClick={load}
                            className="rounded-md border border-gray-300 bg-white px-4 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50"
                        >
                            Search
                        </button>
                    </div>
                </section>

                <section className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
                    <div className="hidden grid-cols-[minmax(260px,2fr)_150px_180px_180px_160px] gap-4 border-b border-gray-200 bg-gray-50 px-5 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500 lg:grid">
                        <span>Migration</span>
                        <span>Status</span>
                        <span>Schedule</span>
                        <span>Progress</span>
                        <span className="text-right">Actions</span>
                    </div>

                    {runs.map(run => {
                        const percent = run.requiredTasks
                            ? Math.round((run.completedTasks * 100) / run.requiredTasks)
                            : 0;
                        return (
                            <article
                                key={run.id}
                                className={`grid gap-4 border-b border-gray-100 px-5 py-4 last:border-b-0 lg:grid-cols-[minmax(260px,2fr)_150px_180px_180px_160px] lg:items-center ${
                                    run.blockedTasks ? 'bg-red-50/40' : 'hover:bg-slate-50'
                                }`}
                            >
                                <div className="min-w-0">
                                    <button
                                        type="button"
                                        onClick={() => openRun(run)}
                                        className="max-w-full truncate text-left font-semibold text-gray-900 hover:text-blue-700"
                                    >
                                        {run.title}
                                    </button>
                                    <p className="mt-1 truncate text-sm text-gray-500">
                                        Owner: {run.ownerName || run.ownerCitizenId}
                                    </p>
                                </div>
                                <div>
                                    <span
                                        className={`rounded-full px-2.5 py-1 text-xs font-semibold ${badge(run.status)}`}
                                    >
                                        {run.status.replace(/_/g, ' ')}
                                    </span>
                                    {run.blockedTasks > 0 && (
                                        <p className="mt-2 text-xs font-semibold text-red-700">
                                            {run.blockedTasks} blocked
                                        </p>
                                    )}
                                </div>
                                <p className="text-sm text-gray-600">
                                    {formatWindow(run.plannedStartAt)}
                                </p>
                                <div>
                                    <div className="h-2 overflow-hidden rounded-full bg-gray-200">
                                        <div
                                            className={`h-full ${run.blockedTasks ? 'bg-red-500' : 'bg-blue-600'}`}
                                            style={{ width: `${percent}%` }}
                                        />
                                    </div>
                                    <p className="mt-1 text-xs text-gray-500">
                                        {run.completedTasks}/{run.requiredTasks} required, {percent}
                                        %
                                    </p>
                                </div>
                                <div className="flex justify-start gap-2 lg:justify-end">
                                    <button
                                        type="button"
                                        onClick={() => openRun(run)}
                                        className="rounded-md bg-blue-600 px-3 py-2 text-sm font-semibold text-white hover:bg-blue-700"
                                    >
                                        {run.status === 'draft' && run.role !== 'viewer'
                                            ? 'Plan'
                                            : 'Open'}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={async () => {
                                            try {
                                                loader.show();
                                                const copy = await copyRun(
                                                    run.id,
                                                    actor(identity, run.versionNo)
                                                );
                                                history.push(
                                                    `/migration-runbook/runs/${copy.id}/manage`
                                                );
                                            } catch (error) {
                                                Swal.fire(
                                                    'Clone failed',
                                                    (error as Error).message,
                                                    'error'
                                                );
                                            } finally {
                                                loader.hide();
                                            }
                                        }}
                                        className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                                    >
                                        Clone
                                    </button>
                                </div>
                            </article>
                        );
                    })}

                    {runs.length === 0 && (
                        <div className="px-6 py-14 text-center">
                            <p className="font-semibold text-gray-700">
                                No migrations in this view
                            </p>
                            <p className="mt-1 text-sm text-gray-500">
                                Change the filter or create a new migration checklist.
                            </p>
                        </div>
                    )}
                </section>
            </div>
        </div>
    );
}
